import React, { useState, useEffect } from "react";
import { Grid, Box, Button, Typography, CircularProgress, TextField, FormControlLabel, Checkbox, IconButton, Icon } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import { useCompany } from "../../contexts/CompanyContext";
import { createTransformation, getTransformationById, updateTransformation } from "../../api/transformations/index.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { getProductList } from "../../api/products";
import { getUserWarehousesByUserId } from "../../api/userWarehouses";
import { useUser } from "../../context/UserContext";

interface ExpectedProduct {
  productId: number | null;
  quantity: number;
  description: string;
}

interface TransformationFormProps {
  onSubmit: () => void;
  id?: number;
}

const validationSchema = Yup.object().shape({
  companyId: Yup.number().required("La empresa es requerida"),
  existingProductId: Yup.number().required("El producto existente es requerido"),
  warehouseId: Yup.number().required("El almacén es requerido"),
  quantity: Yup.number()
    .required("La cantidad es requerida")
    .min(1, "La cantidad debe ser mayor que 0"),
  expectedProducts: Yup.array().of(
    Yup.object().shape({
      productId: Yup.number().required("El producto es requerido"),
      quantity: Yup.number()
        .required("La cantidad es requerida")
        .min(1, "La cantidad debe ser mayor que 0"),
      description: Yup.string()
    })
  ).min(1, "Debe agregar al menos un producto esperado"),
});

const TransformationForm: React.FC<TransformationFormProps> = ({ onSubmit, id }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<any>({
    companyId: null,
    existingProductId: null,
    warehouseId: null,
    quantity: 0,
    expectedProducts: []
  });
  const { selectedCompany } = useCompany();
  const { userData } = useUser();
  const { openSnack } = useSnack();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getTransformationById(id);
      const data = response.data;

      // Convert inbound items to expected products format
      let convertedProducts: ExpectedProduct[] = [];
      if (data.inbound?.inboundItems && Array.isArray(data.inbound.inboundItems)) {
        convertedProducts = data.inbound.inboundItems.map((item: any) => ({
          productId: item.productId,
          quantity: item.quantityDelivered,
          description: item.description || ""
        }));
      }

      setFormData({
        companyId: data.companyId,
        existingProductId: data.existingProductId,
        warehouseId: data.warehouseId,
        quantity: data.quantity,
        expectedProducts: convertedProducts
      });
    } catch (error) {
      console.log(error);
      openSnack("Error al cargar la transformación", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formMode === "edit" && id) {
      fetchData();
    } else {
      setFormData({
        companyId: selectedCompany.id,
        existingProductId: null,
        warehouseId: null,
        quantity: 0,
        expectedProducts: []
      });
      setLoading(false);
    }
  }, [id, selectedCompany.id, formMode]);

  const createPayload = (values: any) => {
    return {
      ...values,
      companyId: selectedCompany.id,
      expectedProducts: values.expectedProducts.map((product: any) => ({
        productId: product.productId,
        quantity: product.quantity,
        description: product.description
      }))
    };
  };

  const handleSubmit = async (values: any) => {
    const payload = createPayload(values);
    try {
      if (id) {
        await updateTransformation(id, payload);
        openSnack("Transformación actualizada correctamente", "success");
      } else {
        await createTransformation(payload);
        openSnack("Transformación creada correctamente", "success");
      }
      onSubmit();
    } catch (error) {
      openSnack("Error al guardar la transformación", "error");
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Formik
      initialValues={{
        ...formData,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, errors }) => (
        <Form>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Transformación de Productos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FastField
                  name="existingProductId"
                  as={DropdownSelector}
                  label="Producto Existente"
                  fetchFunction={getProductList}
                  fetchParams={{ companyId: selectedCompany.id }}
                  optionLabel={["code", "description"]}
                  value={values.existingProductId}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FastField
                  name="warehouseId"
                  as={DropdownSelector}
                  label="Almacén"
                  fetchFunction={getUserWarehousesByUserId}
                  fetchParams={userData.id}
                  optionLabel={"warehouse.name"}
                  value={values.warehouseId}
                  keyField={"warehouseId"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FastField
                  name="quantity"
                  as={TextField}
                  label="Cantidad"
                  type="number"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={values.quantity}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Productos Esperados
                </Typography>
                {values.expectedProducts.map((product, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #aaaaaa",
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      position: "relative"
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={11}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <DropdownSelector
                              name={`expectedProducts[${index}].productId`}
                              label="Producto"
                              fetchFunction={getProductList}
                              fetchParams={{ companyId: selectedCompany.id }}
                              optionLabel={["code", "description"]}
                              value={product.id}
                              keyField="id"

                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Cantidad"
                              type="number"
                              variant="outlined"
                              fullWidth
                              value={product.quantity}
                              onChange={(e) => setFieldValue(`expectedProducts[${index}].quantity`, parseInt(e.target.value) || 0)}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Descripción"
                              variant="outlined"
                              fullWidth
                              value={product.description}
                              onChange={(e) => setFieldValue(`expectedProducts[${index}].description`, e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          onClick={() => {
                            const newProducts = [...values.expectedProducts];
                            newProducts.splice(index, 1);
                            setFieldValue('expectedProducts', newProducts);
                          }}
                          color="error"
                          sx={{ position: "absolute", top: 8, right: 8 }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button
                  variant="contained"
                  onClick={() => {
                    const newProduct = {
                      productId: null,
                      quantity: 1,
                      description: ""
                    };
                    setFieldValue('expectedProducts', [...values.expectedProducts, newProduct]);
                  }}
                  sx={{ mt: 1 }}
                >
                  Agregar Producto Esperado
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {formMode === "edit" ? "Actualizar" : "Guardar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default TransformationForm; 